export const USER_KINDS = ["ADMIN", "CUSTOMER"] as const;
export type UserKind = (typeof USER_KINDS)[number];

export const USER_STATUSES = ["ACTIVE", "DISABLED"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const CUSTOMER_STATUSES = ["ACTIVE", "SUSPENDED", "CLOSED"] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const JOB_STATUSES = ["QUEUED", "RUNNING", "WAITING", "SUCCESS", "FAILED", "CANCELLED"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = ["SUCCESS", "FAILED", "CANCELLED"];

/**
 * Job types. Infrastructure job types from the specification are added as their
 * handlers are implemented; a type must never be enqueued without a handler.
 */
export const JOB_TYPES = [
  "MAINTENANCE_CLEANUP", "SERVER_TEST_CONNECTION", "PROVISION_SERVER", "UPDATE_SERVER",
  "CREATE_WEBSITE", "DELETE_WEBSITE", "DEPLOY_APPLICATION", "CREATE_DATABASE", "DELETE_DATABASE", "ISSUE_SSL", "GIT_DEPLOY",
  "WORDPRESS_CLONE", "WORDPRESS_PUSH_STAGING", "WORDPRESS_RESTORE", "WORDPRESS_UPDATE",
  "PACKAGE_LIMITS_APPLY", "MIGRATION_DISCOVER", "MIGRATION_RUN",
  "BACKUP_RUN", "BACKUP_RESTORE", "SYNC_PROXY", "SECURITY_SCAN",
  "APP_INSTALL", "APP_UPDATE", "APP_BACKUP", "APP_RESTORE", "APP_UNINSTALL", "APP_HEALTH",
] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const AUDIT_RESULTS = ["SUCCESS", "FAILURE", "DENIED"] as const;
export type AuditResult = (typeof AUDIT_RESULTS)[number];

export const ACTOR_TYPES = ["ADMIN", "CUSTOMER", "SYSTEM"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

/** Why a signed-in session is currently limited to a subset of endpoints. */
export const SESSION_RESTRICTIONS = ["PASSWORD_CHANGE_REQUIRED", "MFA_ENROLLMENT_REQUIRED"] as const;
export type SessionRestriction = (typeof SESSION_RESTRICTIONS)[number];

export const AUDIT_ACTIONS = [
  "AUTH_LOGIN",
  "AUTH_LOGIN_FAILED",
  "AUTH_LOCKED",
  "AUTH_LOGOUT",
  "AUTH_MFA_FAILED",
  "PASSWORD_CHANGED",
  "PASSWORD_RESET",
  "MFA_ENABLED",
  "MFA_DISABLED",
  "MFA_RESET",
  "MFA_RECOVERY_CODE_USED",
  "MFA_RECOVERY_CODES_REGENERATED",
  "SESSION_REVOKED",
  "SESSIONS_REVOKED",
  "PERMISSION_DENIED",
  "ADMIN_CREATED",
  "ADMIN_UPDATED",
  "ADMIN_DISABLED",
  "ADMIN_ENABLED",
  "ROLE_CREATED",
  "ROLE_UPDATED",
  "ROLE_DELETED",
  "ROLE_CHANGED",
  "CUSTOMER_CREATED",
  "CUSTOMER_UPDATED",
  "CUSTOMER_SUSPENDED",
  "CUSTOMER_ACTIVATED",
  "CUSTOMER_CLOSED",
  "PROFILE_UPDATED",
  "SETTINGS_UPDATED",
  "JOB_CANCELLED",
  "JOB_RETRIED",
  "REGION_CREATED",
  "REGION_UPDATED",
  "SERVER_CONNECTION_TESTED",
  "SERVER_ADDED",
  "SERVER_UPDATED",
  "SERVER_PROVISIONED",
  "SERVER_PROVISIONING_FAILED",
  "SERVER_PROVISIONING_RETRIED",
  "SERVER_UPDATE_STARTED",
  "SERVER_ACTIVATED",
  "SERVER_MAINTENANCE_ENABLED",
  "SERVER_MAINTENANCE_DISABLED",
  "SERVER_REBOOTED",
  "SERVER_DIAGNOSTICS_RUN",
  "SERVER_LOGS_VIEWED",
  "SERVER_RETIRED",
  "SERVER_REMOVED",
  "AGENT_ENROLLED",
  "AGENT_ENROLLMENT_REJECTED",
  "AGENT_CERT_RENEWED",
  "AGENT_ROTATION_REQUESTED",
  "PLAN_CREATED",
  "PLAN_UPDATED",
  "SUBSCRIPTION_CREATED",
  "SUBSCRIPTION_UPDATED",
  "WEBSITE_CREATED",
  "WEBSITE_UPDATED",
  "WEBSITE_SUSPENDED",
  "WEBSITE_RESUMED",
  "WEBSITE_RESTARTED",
  "WEBSITE_DELETED",
  "DOMAIN_ADDED",
  "DOMAIN_REMOVED",
  "DOMAIN_VERIFIED",
  "DATABASE_CREATED",
  "DATABASE_DELETED",
  "DATABASE_PASSWORD_CHANGED",
  "REDIS_ENABLED",
  "REDIS_DISABLED",
  "FILES_ACCESSED",
  "FILES_MODIFIED",
  "SFTP_KEY_ADDED",
  "SFTP_KEY_REMOVED",
  "SFTP_PASSWORD_RESET",
  "SFTP_PASSWORD_DISABLED",
  "CRON_CREATED",
  "CRON_UPDATED",
  "CRON_DELETED",
  "CRON_RUN_TRIGGERED",
  "GIT_CONNECTED",
  "GIT_UPDATED",
  "GIT_DISCONNECTED",
  "GIT_DEPLOY_TRIGGERED",
  "PHPMYADMIN_SSO",
  "WORDPRESS_LOGIN",
  "WORDPRESS_SETTINGS_CHANGED",
  "WORDPRESS_EXTENSION_CHANGED",
  "WORDPRESS_CLI",
  "WORDPRESS_PASSWORD_RESET",
  "WORDPRESS_UPDATE_STARTED",
  "WORDPRESS_CLONED",
  "WORDPRESS_STAGING_CREATED",
  "WORDPRESS_STAGING_PUSHED",
  "WORDPRESS_RESTORED",
  "MIGRATION_DETECTED",
  "MIGRATION_CONNECTION_TESTED",
  "MIGRATION_CREATED",
  "MIGRATION_STARTED",
  "MIGRATION_RETRIED",
  "MIGRATION_CANCELLED",
  "MIGRATION_DELETED",
  "MIGRATION_COMPLETED",
  "MIGRATION_FAILED",
  "MIGRATION_CREDENTIALS_DESTROYED",
  "MIGRATION_ACCESS_UPDATED",
  "MIGRATION_DNS_IMPORTED",
  "MIGRATION_EMAIL_RESYNCED",
  "MAIL_DOMAIN_CREATED",
  "MAIL_DOMAIN_DELETED",
  "MAILBOX_CREATED",
  "MAILBOX_PASSWORD_CHANGED",
  "MAILBOX_UPDATED",
  "MAILBOX_DELETED",
  "MAIL_ALIAS_SAVED",
  "MAIL_ALIAS_DELETED",
  "WEBMAIL_SSO",
  "API_TOKEN_CREATED",
  "API_TOKEN_REVOKED",
  "API_TOKEN_DENIED",
  "WEBHOOK_CREATED",
  "WEBHOOK_UPDATED",
  "WEBHOOK_DELETED",
  "WEBHOOK_TESTED",
  "DNS_ZONE_CONNECTED",
  "DNS_ZONE_DISCONNECTED",
  "DNS_CREDENTIAL_UPDATED",
  "DNS_RECORD_CREATED",
  "DNS_RECORD_UPDATED",
  "DNS_RECORD_DELETED",
  "BACKUP_DESTINATION_CREATED",
  "BACKUP_DESTINATION_UPDATED",
  "BACKUP_DESTINATION_DELETED",
  "BACKUP_CREATED",
  "BACKUP_DELETED",
  "BACKUP_RESTORE_STARTED",
  "SECURITY_IP_RULES_UPDATED",
  "SECURITY_SCAN_STARTED",
  "SECURITY_SCAN_SCHEDULE_UPDATED",
  "APP_INSTALL_STARTED",
  "APP_UPDATE_STARTED",
  "APP_BACKUP_STARTED",
  "APP_RESTORE_STARTED",
  "APP_UNINSTALL_STARTED",
  "APP_INSTALLED",
  "APP_UPDATED",
  "APP_BACKED_UP",
  "APP_RESTORED",
  "APP_UNINSTALLED",
  "APP_CREDENTIALS_REVEALED",
  "APP_SETTINGS_CHANGED",
  "APP_RELEASE_APPROVED",
  "APP_RELEASE_REVOKED",
  "SECURITY_FINDING_STATUS_CHANGED",
  "SECURITY_FILE_QUARANTINED",
  "SECURITY_FILE_RESTORED",
  "SECURITY_QUARANTINE_DELETED",
  "SERVER_DRAIN_STARTED",
  "SERVER_DRAIN_STOPPED",
  "BILLING_SUSPENDED",
  "BILLING_KEY_CREATED",
  "BILLING_KEY_REVOKED",
  "CUSTOMER_BILLING_LINKED",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
